import { Box, Card, CardContent, Typography, Stack, Grid } from "@mui/material";
import { Donut } from "../../v2/insights/Summary/componentUtils/Donut";
import { DbUser, InsightsCtx, UserCtx } from "../../../interfaces";
import { makeStyles } from "@mui/styles";
import { UserContext } from "../../../context/UserContext";
import { useContext } from "react";
import { InsightsContext } from "../../../context/InsightsContext";

const useStyle = makeStyles((theme) => ({
  fafwIcon: {
    "&:before": {
      width: "14px",
      height: "12px",
    },
  },
  cardContainer: {
    minWidth: "378px",
    minHeight: "104px",
    width: "100%",
    padding: "24px",
    transition: "box-shadow 200ms ease-in-out",
    cursor: "pointer",
    "&:hover": {
      boxShadow: "0px 8px 12px #00000029",
    },
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  gridContainer: {
    padding: "0px 24px",
    height: "68px",
    width: "100%",
    borderTop: `2px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "background-color 200ms ease-in-out",
    "&:hover": {
      backgroundColor: "#0000000A",
      "& .title": {
        textDecoration: "underline",
      },
    },
  },
  gridRow: {
    padding: "0px",
  },
  gridItem: {
    padding: "0px",
    display: "flex",
    alignItems: "center",
  },

  profileCircle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "56px",
    height: "56px",
    backgroundColor: "#E2F7FF",
    borderRadius: "50%",
    margin: "0 auto",
    position: "relative",
  },
  initials: {
    fontSize: "25px",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
}));

interface UserListCardProps {
  user: Partial<DbUser>;
  percentage?: number;
  variant?: "card" | "grid";
  onClick: (navigateTo: string, userTitle: string) => void;
}

export const UserListCard = (props: UserListCardProps) => {
  const { user, variant = "card", percentage = 0, onClick } = props;
  const classes = useStyle();
  const userCtx = useContext(UserContext) as UserCtx;
  const userClass =
    user.observer_details?.class ?? (user.class_student && user.class_student[0]?.class);
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;

  const handleClick = () => {
    if (!user.class_student || !user.id || !user.observer_details?.class_id) return;
    onClick(
      insightsCtx.getStudentUrl(user.id, user.observer_details?.class_id),
      user.full_name!
    );
  };

  const initials =
    user.first_name &&
    user.last_name &&
    (user.first_name[0] + user.last_name[0]).toUpperCase();

  if (variant === "grid") {
    return (
      <Stack
        direction="row"
        alignItems="center"
        className={classes.gridContainer}
        onClick={handleClick}>
        <Typography
          variant="body1"
          fontWeight={600}
          component="div"
          sx={{ minWidth: "280px", marginRight: "24px" }}
          className={`title ${classes.ellipsisText}`}>
          {user.full_name}
        </Typography>
        <Grid container className={classes.gridRow}>
          <Grid item xs={4} className={classes.gridItem}>
            <Typography
              className={classes.ellipsisText}
              variant="body2"
              component="span"
              fontSize="14px">
              {userClass?.course?.name}
            </Typography>
          </Grid>
          <Grid item xs={4} className={classes.gridItem}>
            <Typography
              className={classes.ellipsisText}
              variant="body2"
              component="span"
              fontSize="14px">
              {userClass?.user?.full_name}
            </Typography>
          </Grid>
          <Grid item xs={4} className={classes.gridItem}>
            <Typography
              sx={{ marginLeft: "auto" }}
              className={classes.ellipsisText}
              variant="body2"
              component="span"
              fontSize="14px">
              {user.observer_ids?.includes(userCtx.dbUser?.id) ? (
                <span>
                  <i
                    className={`far fa-eye fa-fw ${classes.fafwIcon}`}
                    style={{ marginRight: "8" }}></i>{" "}
                  Observer
                </span>
              ) : (
                ""
              )}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    );
  } else {
    return (
      <Card className={classes.cardContainer} onClick={handleClick}>
        <CardContent
          sx={{
            height: "100%",
            padding: "0px !important",
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
          }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "16px", width: "100%" }}>
            <Box className={classes.profileCircle}>
              <Typography component="span" variant="h1" className={classes.initials}>
                {initials}
              </Typography>
            </Box>
            <Stack
              direction="column"
              sx={{
                flexGrow: 1,
                flexBasis: "0",
                flexShrink: 1,
                overflow: "hidden",
                marginRight: "auto",
              }}>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  width: "100%",
                  marginBottom: "6px",
                }}
                className={classes.ellipsisText}
                variant="h4"
                component="span">
                {user.full_name}
              </Typography>
              <Stack direction="row">
                <Typography
                  sx={{
                    fontSize: "14px",
                    marginRight: "26px",
                  }}
                  variant="body2"
                  component="span">
                  <i
                    className={`fa-regular fa-book fa-fw ${classes.fafwIcon}`}
                    style={{ marginRight: "10px" }}></i>{" "}
                  {userClass?.course?.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                  }}
                  variant="body2"
                  component="span">
                  <i
                    className={`fa-regular fa-user fa-fw ${classes.fafwIcon}`}
                    style={{ marginRight: "10px" }}></i>{" "}
                  {`${userClass?.user?.full_name} (${userClass?.subject} ${userClass?.period_block})`}
                </Typography>
              </Stack>
            </Stack>
            <Donut
              medium={true}
              value={Math.round(percentage * 100)}
              label={"Label"}
              aria-label={"Label"}
            />
          </Box>
        </CardContent>
      </Card>
    );
  }
};
